import {
  DashboardMultiSelectFilter,
  DashboardSingleSelectFilter,
  MultiSelectFilterSettings,
  MultiSelectFilterUISettings,
  SingleSelectFilterSettings,
  SingleSelectFilterUISettings
} from "./dashboard_select_filter";
import {DSVRowArray, DSVRowString, range} from "d3";
import {
  DashboardFilterApplicator,
  LocalRowColumnArrayFilterValueFilterApplicator,
  LocalRowColumnSingleFilterValueFilterApplicator,
  LocalRowColumnValueInRangeFilterApplicator,
  PeriodRangeFilterValueFilterApplicator,
  PeriodTypeFilterValueFilterApplicator,
  PeriodValueFilterValueFilterApplicator,
  RemoteFilterApplicator
} from "./dashboard_filter_applicators";
import {
  MultiSelectParentSelectChildFilterSettings,
  SingleSelectParentSelectChildFilterSettings
} from "./dashboard_filter_parent_filter_settings";
import {DashboardFilterSelectOption} from "./dashboard_filter_select_option";
import {LocalDatasetsService} from "../../../shared/services/local-datasets.service";
import {DatasetsService} from "../../../shared/services/datasets.service";
import {DashboardRangeFilter, DashboardSliderFilter, RangeFilterSettings, RangeFilterUISettings} from "./dashboard_range_filter";
import {ActivatedRoute, Data, Router} from "@angular/router";
import {Store} from "@ngxs/store";
import {Product} from "../../../shared/graphql/generated/graphql";
import {AuthState} from "../../../state/auth/auth.state";
import {DashboardFilter} from "./dashboard_filter";
import {DashboardFilterUISettings} from "./dashboard_filter_ui_settings";
import { ProductMethodologiesLimiterService } from "../services/product-methodologies-limiter.service";
import { generatePeriodOptions } from "src/app/shared/utils/generate-year-granularity-maps";
import { DashboardPeriodRangeFilterBase, PeriodRangeFilterSettings, PeriodRangeFilterUISettings } from "./dashboard_period_range_filter";
export { DashboardPeriodRangeFilterBase, PeriodRangeFilterSettings, PeriodRangeFilterUISettings } from "./dashboard_period_range_filter";
import { DashboardPeriodSingleSelectFilterBase } from "./dashboard_period_select_filter";


/**
 * Reads a dataset's year/date metadata from the user's accessible datasets.
 * A missing entry makes the automatic time filters fall back to empty defaults
 * (year `0`, no options), which silently leaves the dashboard without data —
 * so make it visible in the console instead.
 */
function getAccessibleDatasetElseWarn(store: Store, datasetName: string) {
  const accessibleDataset = store.selectSnapshot(AuthState.accessibleDataset(datasetName));

  if (!accessibleDataset) {
    console.warn(`[filters] "${datasetName}" is not part of the user's accessible datasets - time filters fall back to empty defaults and the dashboard will show no data.`);
  }

  return accessibleDataset;
}


function createDatasetServiceWrapper(superclass: any) {
  return class DatasetServiceWrapper extends superclass {

    constructor(...args: any[]) {
      super(arguments);
    }

    _datasetService?: DatasetsService;

    get datasetService(): DatasetsService {
      if (!this._datasetService) {
        throw new Error('datasetService not set');
      }
      return this._datasetService;
    }

    setDatasetService(datasetService: DatasetsService) {
      this._datasetService = datasetService;
    }
  }
}


export class DashboardYearSingleSelectFilter extends DashboardSingleSelectFilter<string> {
  constructor({
    minYear,
    maxYear,
    defaultValue,
    name = 'year',
    applicator = new LocalRowColumnSingleFilterValueFilterApplicator('year'),
                showInFilterComponent = true,
                showWhenDisabled = false
  }: {
    minYear: number, maxYear: number, defaultValue?: number, name?: string,
    applicator?: DashboardFilterApplicator<string, DSVRowArray<string>, DSVRowString>,
    showInFilterComponent?: boolean,
    showWhenDisabled?: boolean
  }) {
    if (minYear > maxYear) {
      throw new Error('minYear must be less than or equal to maxYear');
    }
    super({
      name: name,
      options: range(minYear, maxYear + 1).reverse().map(year => {
        return {
          label: year.toString(),
          value: year.toString(),
        }
      }),
      settings: new SingleSelectFilterSettings({}),
      uiSettings: new SingleSelectFilterUISettings({
        label: 'Jahr',
        showInFilterComponent: showInFilterComponent,
        showWhenDisabled: showWhenDisabled
      }),
      applicator: applicator,
      initialValue: (defaultValue ?? maxYear).toString(),
    });
  }
}


export class DashboardYearSliderFilter extends DashboardRangeFilter<number> {
  constructor(store: Store, datasetName: string, {
    defaultValue,
    name = 'year',
    applicator = new LocalRowColumnValueInRangeFilterApplicator('year'),
    showInFilterComponent = true,
    showWhenDisabled = false,
    maxYearOffset = 0,
    minYearOffset = 0,
  }: {
    defaultValue?: number,
    name?: string,
    applicator?: DashboardFilterApplicator<number[], DSVRowArray<string>, DSVRowString>,
    showInFilterComponent?: boolean,
    showWhenDisabled?: boolean,
    maxYearOffset?: number,
    minYearOffset?: number,
  }) {
    const accessibleDataset = getAccessibleDatasetElseWarn(store, datasetName);
    const [minYear, maxYearUser, maxYearDataset] = accessibleDataset ? accessibleDataset.years : [0, 0, 0];

    if (minYear > maxYearUser) {
      throw new Error('minYear must be less than or equal to maxYear');
    }

    if (defaultValue != null && defaultValue < minYear) {
      throw new Error('defaultValue must be greater than or equal to minYear');
    }

    if (defaultValue != null && defaultValue > maxYearUser) {
      throw new Error('defaultValue must be less than or equal to maxYear');
    }

    const minYearFinal = minYear + minYearOffset
    const maxYearFinal = maxYearUser + maxYearOffset;

    super({
      name: name,
      initialValue: defaultValue ?? maxYearFinal,
      parentFilterSettings: undefined,
      uiSettings: {
        label: 'Jahr',
        showInFilterComponent: showInFilterComponent,
        showWhenDisabled: showWhenDisabled
      },
      settings: {
        min: minYearFinal,
        max: maxYearFinal,
      },
      applicator: applicator
    });
  }
}

export class DashboardYearsRangeFilter extends DashboardRangeFilter<number[]> {
  constructor({
    minYear,
    maxYear,
    defaultMinYear,
    defaultMaxYear,
    name = 'years',
    applicator = new LocalRowColumnValueInRangeFilterApplicator('year'),
                showInFilterComponent = true,
                showWhenDisabled = false
  }: {
    minYear: number,
    maxYear: number,
    defaultMinYear?: number,
    defaultMaxYear?: number,
    name?: string,
    applicator?: DashboardFilterApplicator<number[], DSVRowArray<string>, DSVRowString>,
    showInFilterComponent?: boolean,
    showWhenDisabled?: boolean
  }) {
    if (minYear > maxYear) {
      throw new Error('minYear must be less than or equal to maxYear');
    }

    if (defaultMinYear != null && defaultMinYear < minYear) {
      throw new Error('defaultMinYear must be greater than or equal to minYear');
    }

    if (defaultMaxYear != null && defaultMaxYear > maxYear) {
      throw new Error('defaultMaxYear must be less than or equal to maxYear');
    }

    const defaultMin = defaultMinYear ?? minYear;
    const defaultMax = defaultMaxYear ?? maxYear;

    super({
      name: name,
      initialValue: [defaultMin, defaultMax],
      parentFilterSettings: undefined,
      uiSettings: new RangeFilterUISettings({
        label: 'Jahre',
        showInFilterComponent,
        showWhenDisabled,
      }),
      settings: new RangeFilterSettings({
        min: minYear,
        max: maxYear,
      }),
      applicator: applicator
    });
  }
}

export class DashboardAutomaticYearsRangeFilter extends DashboardYearsRangeFilter {
  constructor(store: Store, datasetName: string, {
    defaultMinYearOffset,
    defaultMaxYearOffset,
    defaultYearRange,
    name = 'years',
    applicator = new LocalRowColumnValueInRangeFilterApplicator('year'),
    showInFilterComponent = true,
    showWhenDisabled = false
  }: {
    defaultMinYearOffset?: number,
    defaultMaxYearOffset?: number,
    defaultYearRange?: number,
    name?: string,
    applicator?: DashboardFilterApplicator<number[], DSVRowArray<string>, DSVRowString>,
    showInFilterComponent?: boolean,
    showWhenDisabled?: boolean
  }) {

    if (defaultYearRange != null && defaultMinYearOffset != null && defaultMaxYearOffset != null) {
      throw new Error('defaultYearRange, defaultMinYearOffset and defaultMaxYearOffset are mutually exclusive');
    }

    const accessibleDataset = getAccessibleDatasetElseWarn(store, datasetName);
    const [minYear, maxYearUser, maxYearDataset] = accessibleDataset ? accessibleDataset.years : [0, 0, 0];

    let defaultMinYear = minYear;
    let defaultMaxYear = maxYearUser;

    if (defaultMinYearOffset != null) {
      defaultMinYear += defaultMinYearOffset;
    }
    if (defaultMaxYearOffset != null) {
      defaultMaxYear += defaultMaxYearOffset;
    }
    if (defaultYearRange != null && defaultMinYearOffset != null) {
      defaultMaxYear = defaultMinYear + defaultYearRange;
    } else if (defaultYearRange != null) {
      defaultMinYear = defaultMaxYear - defaultYearRange;
    }

    if (defaultMinYear < minYear) {
      defaultMinYear = minYear;
    }
    if (defaultMaxYear > maxYearUser) {
      defaultMaxYear = maxYearUser;
    }

    super({
      minYear,
      maxYear: maxYearUser,
      defaultMinYear,
      defaultMaxYear,
      name,
      applicator,
      showInFilterComponent,
      showWhenDisabled
    });
  }
}

export class DashboardPeriodTypeSingleSelectFilter extends DashboardSingleSelectFilter<string> {
  constructor(args: {
    name?: string,
    label?: string,
    showWhenDisabled?: boolean,
    showInFilterComponent?: boolean,
    showLabel?: boolean,
    renderAtChildFilterComponent?: boolean,
    showFullOptionLabel?: boolean,
    excludedOptions?: string[]
  }) {
    const optionsAvailable = [
      {
        label: args.showFullOptionLabel || args.showFullOptionLabel === undefined ? 'Jahre' : 'Y',
        value: 'year'
      },
      {
        label: args.showFullOptionLabel || args.showFullOptionLabel === undefined ? 'Quartale' : 'Q',
        value: 'quarter',
      },
      {
        label: args.showFullOptionLabel || args.showFullOptionLabel === undefined ? 'Monthly' : 'M',
        value: 'month',
      },
    ].filter(opt => args.excludedOptions ? !args.excludedOptions.includes(opt.value) : true);

    super({
      name: args.name ?? 'period',
      options: optionsAvailable,
      uiSettings: new SingleSelectFilterUISettings({
        label: args.label ?? 'Zeitpunkt',
        showLabel: args.showLabel ?? true,
        showInFilterComponent: args.showInFilterComponent ?? true,
        showWhenDisabled: args.showWhenDisabled,
        renderAtChildFilterComponent: args.renderAtChildFilterComponent ?? false,
      }),
      settings: new SingleSelectFilterSettings({}),
      initialValue: 'year',
      applicator: new PeriodTypeFilterValueFilterApplicator()
    });
  }
}

export class DashboardPeriodSingleSelectFilter extends DashboardPeriodSingleSelectFilterBase<string | undefined>  {
  constructor(store: Store, datasetName: string, {
    name = 'period_value',
    showInFilterComponent = true,
    showWhenDisabled = false,
    minYearOffset = 0,
    maxYearOffset = 0,
    excludedOptions = [],
    parentFilterName = 'period',
    showLabel = true,
    initialValue,
    minDate,
    maxDate,
  }: {
    name?: string,
    showInFilterComponent?: boolean,
    showWhenDisabled?: boolean,
    showLabel?: boolean,
    minYearOffset?: number
    maxYearOffset?: number,
    excludedOptions?: string[],
    parentFilterName?: string,
    initialValue?: string,
    minDate?: Date | string,
    maxDate?: Date | string,
  }) {
    const accessibleDataset = getAccessibleDatasetElseWarn(store, datasetName);
    const [minYear, maxYearUser, maxYearDataset] = accessibleDataset ? accessibleDataset.years : [0, 0, 0];

    let adjustedMinYear = minYear + (minYearOffset || 0);
    let adjustedMaxYear = maxYearUser + (maxYearOffset || 0);

    const minDateParsed = minDate != null ? new Date(minDate) : undefined;
    const maxDateParsed = maxDate != null ? new Date(maxDate) : undefined;

    if (minDateParsed && maxDateParsed && !Number.isNaN(minDateParsed.getTime()) && !Number.isNaN(maxDateParsed.getTime())) {
      adjustedMinYear = minDateParsed.getFullYear();
      adjustedMaxYear = maxDateParsed.getFullYear();
    }

    const options = generatePeriodOptions(adjustedMinYear, adjustedMaxYear, {
      minDate: minDateParsed ?? minDate,
      maxDate: maxDateParsed ?? maxDate,
    })
      .filter(option => !excludedOptions.includes(option.period_id))
      .reverse();

    super({
      name: name,
      options,
      settings: new SingleSelectFilterSettings({}),
      uiSettings: new SingleSelectFilterUISettings({
        label: 'Zeiteinheit',
        showInFilterComponent: showInFilterComponent,
        showWhenDisabled: showWhenDisabled,
        showLabel
      }),
      applicator: new PeriodValueFilterValueFilterApplicator(),
      initialValue,
      parentFilterSettings: parentFilterName
        ? new SingleSelectParentSelectChildFilterSettings({ parentFilterName, filterByKey: 'period' })
        : undefined
    });
  }
}

export class DashboardAutomaticPeriodSingleSelectFilter extends DashboardPeriodSingleSelectFilter {
  constructor(store: Store, datasetName: string, {
    name = 'period_value',
    showInFilterComponent = true,
    showWhenDisabled = false,
    minYearOffset = 0,
    maxYearOffset = 0,
    excludedOptions = [],
    parentFilterName = 'period',
    showLabel = true,
    initialValue,
  }: {
    name?: string,
    showInFilterComponent?: boolean,
    showWhenDisabled?: boolean,
    showLabel?: boolean,
    minYearOffset?: number
    maxYearOffset?: number,
    excludedOptions?: string[],
    parentFilterName?: string,
    initialValue?: string,
  }) {
    const accessibleDataset = getAccessibleDatasetElseWarn(store, datasetName);
    const minDate = accessibleDataset?.min_date;
    const maxDate = accessibleDataset?.max_date;

    super(store, datasetName, {
      name,
      showInFilterComponent,
      showWhenDisabled,
      minYearOffset,
      maxYearOffset,
      excludedOptions,
      parentFilterName,
      showLabel,
      initialValue,
      minDate,
      maxDate,
    });
  }
}

export class DashboardPeriodsRangeFilter extends DashboardPeriodRangeFilterBase<string, string> {
  constructor(store: Store, datasetName: string, {
    name = 'periods',
    label = 'Zeitraum',
    minYearOffset,
    maxYearOffset,
    initialRange = 3,
    showInFilterComponent = true,
    showWhenDisabled = false,
    parentFilterName = 'period',
    parentFilterByKey = 'period',
    excludedOptions = [],
    minDate,
    maxDate,
  }: {
    name?: string,
    label?: string,
    minYearOffset?: number,
    maxYearOffset?: number,
    initialRange?: number | 'all',
    showInFilterComponent?: boolean,
    showWhenDisabled?: boolean,
    parentFilterName?: string,
    parentFilterByKey?: string,
    excludedOptions?: string[],
    minDate?: Date | string,
    maxDate?: Date | string,
  }) {
    if (initialRange != null && minYearOffset != null && maxYearOffset != null) {
      throw new Error('initialtYearRange, defaultMinYearOffset and maxYearOffset are mutually exclusive');
    }

    const accessibleDataset = getAccessibleDatasetElseWarn(store, datasetName);
    const [minYear, maxYearUser, maxYearDataset] = accessibleDataset ? accessibleDataset.years : [0, 0, 0];

    let adjustedMinYear = minYear + (minYearOffset || 0);
    let adjustedMaxYear = maxYearUser + (maxYearOffset || 0);

    const minDateParsed = minDate != null ? new Date(minDate) : undefined;
    const maxDateParsed = maxDate != null ? new Date(maxDate) : undefined;

    if (minDateParsed && maxDateParsed && !Number.isNaN(minDateParsed.getTime()) && !Number.isNaN(maxDateParsed.getTime())) {
      adjustedMinYear = minDateParsed.getFullYear();
      adjustedMaxYear = maxDateParsed.getFullYear();
    }

    const options = generatePeriodOptions(adjustedMinYear, adjustedMaxYear, {
      minDate: minDateParsed ?? minDate,
      maxDate: maxDateParsed ?? maxDate,
    }).filter(option => !excludedOptions.includes(option.period_id))

    super({
      name,
      options,
      settings: new PeriodRangeFilterSettings({
        initialRange: initialRange === 'all' ? options.length : initialRange,
        optionLabelKey: 'period_id',
        optionValueKey: 'period_id',
      }),
      uiSettings: new PeriodRangeFilterUISettings({
        label,
        showInFilterComponent,
        showWhenDisabled
      }),
      applicator: new PeriodRangeFilterValueFilterApplicator(),
      initialValue: [],
      parentFilterSettings: parentFilterName ? new SingleSelectParentSelectChildFilterSettings({
        parentFilterName,
        filterByKey: parentFilterByKey,
      }) : undefined
    });
  }
}

export class DashboardAutomaticPeriodsRangeFilter extends DashboardPeriodsRangeFilter {
  constructor(store: Store, datasetName: string, {
    defaultPeriodRange,
    name = 'periods',
    label = 'Zeitraum',
    minYearOffset,
    maxYearOffset,
    initialRange = 3,
    showInFilterComponent = true,
    showWhenDisabled = false,
    parentFilterName = 'period',
    parentFilterByKey = 'period',
    excludedOptions = []
  }: {
    defaultPeriodRange?: number,
    name?: string,
    label?: string,
    minYearOffset?: number,
    maxYearOffset?: number,
    initialRange?: number | 'all',
    showInFilterComponent?: boolean,
    showWhenDisabled?: boolean,
    parentFilterName?: string,
    parentFilterByKey?: string,
    excludedOptions?: string[],
  }) {
    const accessibleDataset = getAccessibleDatasetElseWarn(store, datasetName);
    const minDate = accessibleDataset?.min_date;
    const maxDate = accessibleDataset?.max_date;

    super(store, datasetName, {
      name,
      label,
      minYearOffset,
      maxYearOffset,
      initialRange: defaultPeriodRange ?? initialRange,
      showInFilterComponent,
      showWhenDisabled,
      parentFilterName,
      parentFilterByKey,
      excludedOptions,
      minDate,
      maxDate,
    });
  }
}

export class DashboardAutomaticYearSingleSelectFilter extends DashboardYearSingleSelectFilter {
  constructor(store: Store, datasetName: string, {
    defaultYearOffset,
    name = 'year',
    applicator = new LocalRowColumnSingleFilterValueFilterApplicator('year'),
    showInFilterComponent = true,
    showWhenDisabled = false,
    minYearOffset = 0,
    maxYearOffset = 0
  }: {
    defaultYearOffset?: number, name?: string,
    applicator?: DashboardFilterApplicator<string, DSVRowArray<string>, DSVRowString>,
    showInFilterComponent?: boolean,
    showWhenDisabled?: boolean,
    minYearOffset?: number
    maxYearOffset?: number
  }) {
    const accessibleDataset = getAccessibleDatasetElseWarn(store, datasetName);
    const [minYear, maxYearUser, maxYearDataset] = accessibleDataset && accessibleDataset.years.length
      ? accessibleDataset.years
      : [0, 0, 0];

    let defaultValue = maxYearUser;
    if (defaultYearOffset != null) {
      defaultValue += defaultYearOffset;
    }

    const minYearToUse = minYear + minYearOffset;
    const maxYearToUse = maxYearUser + maxYearOffset;

    super({
      name: name,
      defaultValue,
      minYear: minYearToUse,
      maxYear: maxYearToUse,
      applicator: applicator,
      showInFilterComponent: showInFilterComponent,
      showWhenDisabled: showWhenDisabled
    });
  }
}


export class DashboardStatesMultiSelectFilter extends DashboardMultiSelectFilter<string> {
  constructor(private localDatasetsService: LocalDatasetsService, {
    name = 'states',
    label = 'Bundesländer',
    addSumGermany = true,
    defaultValues = [],
    allInitiallySelected = true,
    allInitiallySelectedExceptValue = [],
    showInFilterComponent = true,
    showWhenDisabled = false,
    applicator = new LocalRowColumnArrayFilterValueFilterApplicator('state_id')
  }: {
    name?: string,
    label?: string,
    addSumGermany?: boolean,
    defaultValues?: string[],
    allInitiallySelected?: boolean,
    allInitiallySelectedExceptValue?: string[],
    showInFilterComponent?: boolean,
    showWhenDisabled?: boolean,
    applicator?: DashboardFilterApplicator<string[], DSVRowArray<string>, DSVRowString>
  }) {
    const options = !addSumGermany
      ? localDatasetsService.getStateOptions().filter(option => option.label !== '∑ Deutschland')
      : localDatasetsService.getStateOptions()

    super({
      name,
      options,
      settings: new MultiSelectFilterSettings({
        allInitiallySelected: allInitiallySelected,
        allInitiallySelectedExceptValue: allInitiallySelectedExceptValue,
      }),
      uiSettings: new MultiSelectFilterUISettings({
        label,
        showInFilterComponent: showInFilterComponent,
        showWhenDisabled: showWhenDisabled
      }),
      applicator,
      initialValue: [...defaultValues],
    });
  }
}


export class DashboardStateSingleSelectFilter extends DashboardSingleSelectFilter<string | undefined> {
  constructor(private localDatasetsService: LocalDatasetsService, {
    name = 'state',
    addSumGermany = true,
    defaultValue,
    showWhenDisabled = false,
    showInFilterComponent = true,
    autoDisplayFirst,
    ignoreProductSetting = false,
    showLabel = true,
    autoSelectByIndex,
    filter,
    applicator = new LocalRowColumnSingleFilterValueFilterApplicator('state_id')
  }: {
    name?: string,
    addSumGermany?: boolean,
    defaultValue?: string,
    showWhenDisabled?: boolean,
    filter?: boolean,
    autoDisplayFirst?: boolean,
    showLabel?: boolean,
    showInFilterComponent?: boolean,
    ignoreProductSetting?: boolean,
    autoSelectByIndex?: number,
    applicator?: DashboardFilterApplicator<string, DSVRowArray<string>, DSVRowString>
  }) {

    const stateOptions = localDatasetsService.getStateOptions()
    const options = !addSumGermany ? stateOptions.filter(option => option.value !== '-1') : stateOptions

    super({
      name,
      options,
      settings: new SingleSelectFilterSettings({}),
      uiSettings: new SingleSelectFilterUISettings({
        label: 'Bundesland',
        placeholder: 'Bundesland auswählen...',
        filter,
        showWhenDisabled,
        showInFilterComponent,
        autoDisplayFirst,
        autoSelectByIndex,
        showLabel
      }),
      applicator: applicator,
      initialValue: defaultValue,
      ignoreProductSetting
    });
  }
}

export class DashboardAdministrativeDistrictSingleSelectFilter extends DashboardSingleSelectFilter<string | undefined> {
  constructor(private localDatasetsService: LocalDatasetsService, {
    defaultValue,
    showWhenDisabled = false,
    showInFilterComponent = true,
    autoDisplayFirst,
    filter,
    applicator = new LocalRowColumnSingleFilterValueFilterApplicator('administrative_district_id'),
    parentFilterName = undefined,
    stateId = undefined
  }: {
    defaultValue?: string,
    showWhenDisabled?: boolean,
    filter?: boolean,
    autoDisplayFirst?: boolean,
    showInFilterComponent?: boolean,
    applicator?: DashboardFilterApplicator<string, DSVRowArray<string>, DSVRowString>,
    parentFilterName?: 'state' | 'states',
    stateId?: string
  }) {

    const options = stateId != null
      ? localDatasetsService.getAdministrativeDistrictsOptionsOfState(stateId)
      : localDatasetsService.getAdministrativeDistrictsOptions()

    super({
      name: 'administrative_district',
      options,
      settings: new SingleSelectFilterSettings({}),
      uiSettings: new SingleSelectFilterUISettings({
        label: 'Regierungsbezirke',
        placeholder: 'Regierungsbezirke auswählen...',
        filter,
        showWhenDisabled,
        showInFilterComponent,
        autoDisplayFirst,
      }),
      applicator: applicator,
      initialValue: defaultValue,
      parentFilterSettings: parentFilterName
        ? new SingleSelectParentSelectChildFilterSettings({ parentFilterName, filterByKey: 'state_id' })
        : undefined
    });
  }
}

export class DashboardRegionalAssociationSingleSelectFilter extends DashboardSingleSelectFilter<string | undefined> {
  constructor(private localDatasetsService: LocalDatasetsService, {
    defaultValue,
    showWhenDisabled = false,
    showInFilterComponent = true,
    autoDisplayFirst,
    filter,
    applicator = new LocalRowColumnSingleFilterValueFilterApplicator('regional_association_id'),
    parentFilterName = undefined,
    stateId = undefined
  }: {
    defaultValue?: string,
    showWhenDisabled?: boolean,
    filter?: boolean,
    autoDisplayFirst?: boolean,
    showInFilterComponent?: boolean,
    applicator?: DashboardFilterApplicator<string, DSVRowArray<string>, DSVRowString>,
    parentFilterName?: 'state' | 'states',
    stateId?: string
  }) {

    const options = stateId != null
      ? localDatasetsService.getRegionalAssociationsOptionsOfState(stateId)
      : localDatasetsService.getRegionalAssociationsOptions()

    super({
      name: 'regional_association',
      options,
      settings: new SingleSelectFilterSettings({}),
      uiSettings: new SingleSelectFilterUISettings({
        label: 'Regionalverbände',
        placeholder: 'Regionalverbände auswählen...',
        filter,
        showWhenDisabled,
        showInFilterComponent,
        autoDisplayFirst,
      }),
      applicator: applicator,
      initialValue: defaultValue,
      parentFilterSettings: parentFilterName
        ? new SingleSelectParentSelectChildFilterSettings({ parentFilterName, filterByKey: 'state_id' })
        : undefined
    });
  }
}

export class DashboardRegionalAssociationsMultiSelectFilter extends DashboardMultiSelectFilter<string> {
  constructor(private localDatasetsService: LocalDatasetsService, {
    allInitiallySelected = true,
    allInitiallySelectedExceptValue = [],
    defaultValues = [],
    showInFilterComponent = true,
    showWhenDisabled = false,
    parentFilterName = undefined,
    stateId = undefined
  }: {
    allInitiallySelected?: boolean, allInitiallySelectedExceptValue?: string[], defaultValues?: string[],
    showInFilterComponent?: boolean, showWhenDisabled?: boolean,
    parentFilterName?: 'state' | 'states',
    stateId?: string
  }) {
    const options = stateId != null ? localDatasetsService.getRegionalAssociationsOptionsOfState(stateId) : localDatasetsService.getRegionalAssociationsOptions()

    super({
      name: 'regional_associations',
      options: options,
      settings: new MultiSelectFilterSettings({
        allInitiallySelected: allInitiallySelected,
        allInitiallySelectedExceptValue: allInitiallySelectedExceptValue,
      }),
      uiSettings: new MultiSelectFilterUISettings({
        label: 'Regionalverbände',
        showInFilterComponent,
        showWhenDisabled
      }),
      applicator: new LocalRowColumnArrayFilterValueFilterApplicator('regional_association_id'),
      initialValue: defaultValues,
      parentFilterSettings: parentFilterName ? new SingleSelectParentSelectChildFilterSettings({
        parentFilterName,
        filterByKey: 'state_id',
      }) : undefined
    });
  }
}

export class DashboardAdministrativeDistrictsMultiSelectFilter extends DashboardMultiSelectFilter<string> {
  constructor(private localDatasetsService: LocalDatasetsService, {
    allInitiallySelected = true,
    allInitiallySelectedExceptValue = [],
    defaultValues = [],
    showInFilterComponent = true,
    showWhenDisabled = false,
    parentFilterName = undefined,
    stateId = undefined
  }: {
    allInitiallySelected?: boolean, allInitiallySelectedExceptValue?: string[], defaultValues?: string[],
    showInFilterComponent?: boolean, showWhenDisabled?: boolean,
    parentFilterName?: 'state' | 'states',
    stateId?: string
  }) {
    const options = stateId != null ? localDatasetsService.getAdministrativeDistrictsOptionsOfState(stateId) : localDatasetsService.getAdministrativeDistrictsOptions()

    super({
      name: 'administrative_districts',
      options: options,
      settings: new MultiSelectFilterSettings({
        allInitiallySelected: allInitiallySelected,
        allInitiallySelectedExceptValue: allInitiallySelectedExceptValue,
      }),
      uiSettings: new MultiSelectFilterUISettings({
        label: 'Regierungsbezirke',
        showInFilterComponent,
        showWhenDisabled
      }),
      applicator: new LocalRowColumnArrayFilterValueFilterApplicator('administrative_district_id'),
      initialValue: defaultValues,
      parentFilterSettings: parentFilterName ? new SingleSelectParentSelectChildFilterSettings({
        parentFilterName,
        filterByKey: 'state_id',
      }) : undefined
    });
  }
}

export class DashboardDistrictsMultiSelectFilter extends DashboardMultiSelectFilter<string> {
  constructor(private localDatasetsService: LocalDatasetsService, {
    name = 'districts',
    label = 'Landkreise / Kreisfreie-Städte',
    parentFilterName = 'states',
    parentFilterByKey = 'state_id',
    defaultValues = [],
    allInitiallySelected = true,
    allInitiallySelectedExceptValue = [],
    allSelectedOnParentChange = false,
    showAllOptionsWhenParentFilterValueIsNull = false,
    showInFilterComponent = true,
    showWhenDisabled = false,
    applicator = new LocalRowColumnArrayFilterValueFilterApplicator('district_id')
  }: {
    name?: string,
    label?: string,
    parentFilterName?: string,
    parentFilterByKey?: string
    defaultValues?: string[],
    allInitiallySelected?: boolean,
    allSelectedOnParentChange?: boolean,
    allInitiallySelectedExceptValue?: string[],
    showInFilterComponent?: boolean,
    showWhenDisabled?: boolean,
    showAllOptionsWhenParentFilterValueIsNull?: boolean
    applicator?: DashboardFilterApplicator<string[], DSVRowArray<string>, DSVRowString>
  }) {
    const options = localDatasetsService.getDistrictOptions();

    super({
      name,
      options: options,
      settings: new MultiSelectFilterSettings({
        allInitiallySelected: allInitiallySelected,
        allInitiallySelectedExceptValue: allInitiallySelectedExceptValue,
        allSelectedOnParentChange
      }),
      uiSettings: new MultiSelectFilterUISettings({
        label,
        showInFilterComponent: showInFilterComponent,
        showWhenDisabled: showWhenDisabled,
        showToggleAllLabel: false,
        filter: true,
        closeForClear: true,
      }),
      applicator,
      initialValue: defaultValues,
      parentFilterSettings: new MultiSelectParentSelectChildFilterSettings({
        parentFilterName,
        filterByKey: parentFilterByKey,
        showAllOptionsWhenParentFilterValueIsNull
      })
    });
  }
}

export class DashboardDistrictSingleSelectFilter extends DashboardSingleSelectFilter<string | undefined> {
  constructor(private localDatasetsService: LocalDatasetsService, {
    defaultValue,
    showWhenDisabled = false,
    showInFilterComponent = true,
    autoDisplayFirst = false,
    autoSelectByIndex,
    showLabel = true,
    ignoreProductSetting = false,
    filter,
    emptyMessage,
    applicator = new LocalRowColumnSingleFilterValueFilterApplicator('district_id')
  }: {
    defaultValue?: string,
    showWhenDisabled?: boolean,
    filter?: boolean,
    emptyMessage?: string,
    autoDisplayFirst?: boolean,
    showInFilterComponent?: boolean,
    autoSelectByIndex?: number,
    showLabel?: boolean,
    ignoreProductSetting?: boolean,
    applicator?: DashboardFilterApplicator<string, DSVRowArray<string>, DSVRowString>
  }) {

    const options = localDatasetsService.getDistrictOptions()

    super({
      name: 'district',
      options,
      settings: new SingleSelectFilterSettings({

      }),
      uiSettings: new SingleSelectFilterUISettings({
        label: 'Landkreis / Kreisfreie-Stadt',
        placeholder: 'Kreis auswählen...',
        emptyMessage,
        showWhenDisabled,
        showInFilterComponent,
        autoDisplayFirst,
        autoSelectByIndex,
        showLabel,
        filter
      }),
      applicator: applicator,
      initialValue: defaultValue,
      ignoreProductSetting,
      parentFilterSettings: new SingleSelectParentSelectChildFilterSettings({
        parentFilterName: 'state',
        filterByKey: 'state_id',
      })
    });
  }
}

export class DashboardRemoteDistrictSingleSelectFilter extends DashboardSingleSelectFilter<string | undefined> {
  constructor(private localDatasetsService: LocalDatasetsService, {
    defaultValue,
    showWhenDisabled = false,
    showInFilterComponent = true,
    autoDisplayFirst = false,
    autoSelectByIndex,
    showLabel = true,
    ignoreProductSetting = false,
    filter,
    emptyMessage,
    applicator = new RemoteFilterApplicator({ remoteFilterKey: 'district_id' }),
    parentFilterName
  }: {
    defaultValue?: string,
    showWhenDisabled?: boolean,
    filter?: boolean,
    emptyMessage?: string,
    autoDisplayFirst?: boolean,
    showInFilterComponent?: boolean,
    autoSelectByIndex?: number,
    showLabel?: boolean,
    ignoreProductSetting?: boolean,
    parentFilterName?: string,
    applicator?: DashboardFilterApplicator<string, DSVRowArray<string>, DSVRowString>
  }) {

    const options = localDatasetsService.getDistrictOptions()

    super({
      name: 'district',
      options,
      settings: new SingleSelectFilterSettings({

      }),
      uiSettings: new SingleSelectFilterUISettings({
        label: 'Landkreis / Kreisfreie-Stadt',
        placeholder: 'Kreis auswählen...',
        emptyMessage,
        showWhenDisabled,
        showInFilterComponent,
        autoDisplayFirst,
        autoSelectByIndex,
        showLabel,
        filter
      }),
      applicator: applicator,
      initialValue: defaultValue,
      ignoreProductSetting,
      parentFilterSettings: parentFilterName
        ? new SingleSelectParentSelectChildFilterSettings({
            parentFilterName: parentFilterName,
            filterByKey: 'state_id',
          })
        : undefined
    });
  }
}

export class DashboardSubMarketsMultiSelectFilter extends DashboardMultiSelectFilter<string> {
  constructor(private localDatasetsService: LocalDatasetsService, {
    name = 'sub_markets',
    allInitiallySelected = true,
    allInitiallySelectedExceptValue = [],
    excludedValues = [],
    invalidSingleSelectionFallback = 'sum-sub-market',
    showInFilterComponent = true,
    showWhenDisabled = false,
    applicator = new LocalRowColumnArrayFilterValueFilterApplicator('sub_market_id')
  }: {
    name?: string,
    allInitiallySelected?: boolean,
    allInitiallySelectedExceptValue?: string[],
    excludedValues?: string[],
    invalidSingleSelectionFallback?: 'sum-sub-market' | 'first-option',
    showInFilterComponent?: boolean,
    showWhenDisabled?: boolean,
    applicator?: DashboardFilterApplicator<string[], DSVRowArray<string>, DSVRowString>
  }) {
    const options = excludedValues && excludedValues.length
      ? localDatasetsService.getSubmarketOptions().filter((option) => !excludedValues.includes(option.value))
      : localDatasetsService.getSubmarketOptions()

    super({
      name,
      options: options,
      settings: new MultiSelectFilterSettings<string, DashboardFilterSelectOption, string | undefined>({
        allInitiallySelected: allInitiallySelected,
        allInitiallySelectedExceptValue: allInitiallySelectedExceptValue,
        preserveSingleSelectionOnParentChange: true,
        fallbackSingleSelectionToFirstOption: invalidSingleSelectionFallback === 'first-option',
        fallbackSingleSelectionOnParentChange: invalidSingleSelectionFallback === 'sum-sub-market'
          ? (methodologyId) => {
            if (methodologyId == null) {
              return undefined;
            }
            const sumSubMarketId = localDatasetsService.getSumSubMarketIdByMethodologyId(Number(methodologyId));
            return sumSubMarketId != null ? sumSubMarketId.toString() : undefined;
          }
          : undefined,
      }),
      uiSettings: new MultiSelectFilterUISettings({
        label: 'Teilmärkte',
        showInFilterComponent,
        showWhenDisabled
      }),
      applicator,
      initialValue: [],
      parentFilterSettings: new SingleSelectParentSelectChildFilterSettings({
        parentFilterName: 'methodology',
        filterByKey: 'methodology_id',
      })
    });
  }
}

export class DashboardIndustriesMultiSelectFilter extends DashboardMultiSelectFilter<string> {
  constructor(private localDatasetsService: LocalDatasetsService, {
    allInitiallySelected = true,
    allInitiallySelectedExceptValue = [],
    showInFilterComponent = true,
    showWhenDisabled = false
  }: { allInitiallySelected?: boolean, allInitiallySelectedExceptValue?: string[], showInFilterComponent?: boolean, showWhenDisabled?: boolean }) {
    const options = localDatasetsService.getIndustriesOptions()

    super({
      name: 'industries',
      options: options,
      settings: new MultiSelectFilterSettings({
        allInitiallySelected: allInitiallySelected,
        allInitiallySelectedExceptValue: allInitiallySelectedExceptValue,
        preserveSingleSelectionOnParentChange: true,
        fallbackSingleSelectionToFirstOption: true,
      }),
      uiSettings: new MultiSelectFilterUISettings({
        label: 'Branchen',
        showInFilterComponent,
        showWhenDisabled
      }),
      applicator: new LocalRowColumnArrayFilterValueFilterApplicator('sub_market_id'),
      initialValue: [],
      parentFilterSettings: new SingleSelectParentSelectChildFilterSettings({
        parentFilterName: 'methodology',
        filterByKey: 'methodology_id',
      })
    });
  }
}

export class DashboardIndustrySingleSelectFilter extends DashboardSingleSelectFilter<string | undefined> {
  constructor(private localDatasetsService: LocalDatasetsService, {
    defaultValue,
    showWhenDisabled = false,
    showInFilterComponent = true,
    filter,
    emptyMessage,
    applicator = new LocalRowColumnSingleFilterValueFilterApplicator('industry_sub_market_id')
  }: {
    defaultValue?: string,
    showWhenDisabled?: boolean,
    filter?: boolean,
    emptyMessage?: string,
    autoDisplayFirst?: boolean,
    showInFilterComponent?: boolean,
    applicator?: DashboardFilterApplicator<string, DSVRowArray<string>, DSVRowString>
  }) {

    const options = localDatasetsService.getIndustriesOptions()

    super({
      name: 'industry',
      options,
      settings: new SingleSelectFilterSettings({

      }),
      uiSettings: new SingleSelectFilterUISettings({
        label: 'Branchen',
        placeholder: 'Branche auswählen...',
        emptyMessage,
        showWhenDisabled,
        showInFilterComponent,
        filter
      }),
      applicator: applicator,
      initialValue: defaultValue,
      parentFilterSettings: new SingleSelectParentSelectChildFilterSettings({
        parentFilterName: 'methodology',
        filterByKey: 'methodology_id',
      })
    });
  }
}


export class DashboardSubMarketSingleSelectFilter extends DashboardSingleSelectFilter<string | undefined> {
  constructor(private localDatasetsService: LocalDatasetsService, {
    name = 'sub_market',
    defaultValue,
    excluded,
    showInFilterComponent = true,
    showWhenDisabled = false,
    autoDisplayFirst,
    autoSelectByIndex,
    filter = false,
    ignoreProductSetting = false,
    showLabel = true,
    emptyMessage,
    placeholder = 'Teilmarkt auswählen...',
    applicator = new LocalRowColumnSingleFilterValueFilterApplicator('sub_market_id')
  }: {
    name?: string,
    defaultValue?: string,
    excluded?: any[],
    showInFilterComponent?: boolean,
    showWhenDisabled?: boolean,
    autoDisplayFirst?: boolean,
    autoSelectByIndex?: number,
    filter?: boolean,
    ignoreProductSetting?: boolean,
    showLabel?: boolean,
    emptyMessage?: string,
    placeholder?: string,
    applicator?: DashboardFilterApplicator<string, DSVRowArray<string>, DSVRowString>
  }) {

    const options = excluded && excluded.length
      ? localDatasetsService.getSubmarketOptions().filter((option) => !excluded.includes(option.value))
      : localDatasetsService.getSubmarketOptions()

    super({
      name,
      options,
      settings: new SingleSelectFilterSettings({}),
      uiSettings: new SingleSelectFilterUISettings({
        label: 'Teilmarkt',
        showLabel,
        showWhenDisabled: showWhenDisabled,
        showInFilterComponent,
        autoDisplayFirst,
        autoSelectByIndex,
        emptyMessage,
        placeholder,
        filter,
      }),
      applicator,
      initialValue: defaultValue,
      parentFilterSettings: new SingleSelectParentSelectChildFilterSettings({
        parentFilterName: 'methodology',
        filterByKey: 'methodology_id',
      }),
      ignoreProductSetting
    });
  }
}

export class DashboardRemoteSubMarketSingleSelectFilter extends DashboardSingleSelectFilter<string | undefined> {
  constructor(private localDatasetsService: LocalDatasetsService, {
    name = 'sub_market',
    defaultValue,
    excluded,
    showWhenDisabled = false,
    autoDisplayFirst,
    filter = false,
    ignoreProductSetting = false,
    emptyMessage,
    placeholder = 'Teilmarkt auswählen...',
  }: {
    name?: string,
    defaultValue?: string,
    excluded?: any[],
    showWhenDisabled?: boolean,
    autoDisplayFirst?: boolean,
    filter?: boolean,
    emptyMessage?: string,
    placeholder?: string,
    ignoreProductSetting?: boolean,
    applicator?: DashboardFilterApplicator<string, DSVRowArray<string>, DSVRowString>
  }) {

    const options = excluded && excluded.length
      ? localDatasetsService.getSubmarketOptions().filter((option) => !excluded.includes(option.value))
      : localDatasetsService.getSubmarketOptions()

    super({
      name,
      options,
      settings: new SingleSelectFilterSettings({}),
      uiSettings: new SingleSelectFilterUISettings({
        label: 'Teilmarkt',
        showWhenDisabled: showWhenDisabled,
        showInFilterComponent: true,
        autoDisplayFirst,
        emptyMessage,
        placeholder,
        filter,
      }),
      applicator: new RemoteFilterApplicator({ remoteFilterKey: 'sub_market_id' }),
      initialValue: defaultValue,
      parentFilterSettings: new SingleSelectParentSelectChildFilterSettings({
        parentFilterName: 'methodology',
        filterByKey: 'methodology_id',
      }),
      ignoreProductSetting
    });
  }
}

export class DashboardWzClassSingleSelectFilter extends DashboardSingleSelectFilter<string | undefined> {
  constructor(private localDatasetsService: LocalDatasetsService, {
    name = 'wz_class',
    parentFilterName = 'sub_market',
    defaultValue,
    excluded,
    showInFilterComponent = true,
    showWhenDisabled = false,
    autoDisplayFirst,
    autoSelectByIndex,
    filter = false,
    showLabel = true,
    emptyMessage,
    placeholder = 'WZ auswählen...',
    applicator = new LocalRowColumnSingleFilterValueFilterApplicator('wz_id'),
    width,
  }: {
    name?: string,
    parentFilterName?: string,
    defaultValue?: string,
    excluded?: any[],
    showInFilterComponent?: boolean,
    showWhenDisabled?: boolean,
    autoDisplayFirst?: boolean,
    autoSelectByIndex?: number,
    filter?: boolean,
    showLabel?: boolean,
    emptyMessage?: string,
    placeholder?: string,
    applicator?: DashboardFilterApplicator<string, DSVRowArray<string>, DSVRowString>
    width?: string
  }) {

    const options = localDatasetsService.getWzClassOptions()

    const parentFilterSettings = parentFilterName.endsWith('s')
      ? new MultiSelectParentSelectChildFilterSettings({
          parentFilterName,
          filterByKey: 'sub_market_id',
        })
      : new SingleSelectParentSelectChildFilterSettings({
          parentFilterName,
          filterByKey: 'sub_market_id',
        })

    super({
      name,
      options,
      settings: new SingleSelectFilterSettings({}),
      uiSettings: new SingleSelectFilterUISettings({
        label: 'WZ-Klasse',
        showWhenDisabled: showWhenDisabled,
        showInFilterComponent,
        showLabel,
        autoDisplayFirst,
        emptyMessage,
        placeholder,
        filter,
        width,
        autoSelectByIndex
      }),
      applicator,
      initialValue: defaultValue,
      parentFilterSettings,
    });
  }
}

export class DashboardMethodologySingleSelectFilter extends DashboardSingleSelectFilter<string | undefined> {
  constructor(private localDatasetsService: LocalDatasetsService, {
    defaultValue,
    onlyShow,
    showInFilterComponent = true,
    autoDisplayFirst,
    emptyMessage,
    placeholder = 'Methodik auswählen...',
    filter,
    width
  }: {
    defaultValue?: string,
    onlyShow?: string[],
    showInFilterComponent?: boolean,
    autoDisplayFirst?: boolean,
    emptyMessage?: string,
    placeholder?: string,
    filter?: boolean,
    width?: string
  }) {
    super({
      name: 'methodology',
      options: localDatasetsService.getMethodologyOptions().filter(option => onlyShow != null ? onlyShow.includes(option.value!) : true),
      settings: new SingleSelectFilterSettings({}),
      uiSettings: new SingleSelectFilterUISettings({
        label: 'Methodik',
        showInFilterComponent,
        placeholder,
        emptyMessage,
        filter,
        autoDisplayFirst,
        width
      }),
      applicator: new RemoteFilterApplicator({remoteFilterKey: 'methodology_id'}),
      initialValue: defaultValue,
    });
  }
}

export class DashboardRemoteStateSingleSelectFilter extends DashboardSingleSelectFilter<string | undefined> {
  constructor(private localDatasetsService: LocalDatasetsService, {
    name = 'state',
    addSumGermany = true,
    defaultValue,
    showWhenDisabled = false,
    showInFilterComponent = true,
    autoDisplayFirst,
    filter,
    ignoreProductSetting = false,
  }: {
    name?: string,
    addSumGermany?: boolean,
    defaultValue?: string,
    showWhenDisabled?: boolean,
    filter?: boolean,
    autoDisplayFirst?: boolean,
    showInFilterComponent?: boolean,
    ignoreProductSetting?: boolean,
  }) {

    const stateOptions = localDatasetsService.getStateOptions()
    const options = !addSumGermany ? stateOptions.filter(option => option.value !== '-1') : stateOptions

    super({
      name,
      options,
      settings: new SingleSelectFilterSettings({}),
      uiSettings: new SingleSelectFilterUISettings({
        label: 'Bundesland',
        placeholder: 'Bundesland auswählen...',
        filter,
        showWhenDisabled,
        showInFilterComponent,
        autoDisplayFirst,
      }),
      applicator: new RemoteFilterApplicator({remoteFilterKey: 'state_id'}),
      initialValue: defaultValue,
      ignoreProductSetting
    });
  }
}


export class DashboardLastYearAvailableToUserRemoteFilter extends DashboardFilter<string | undefined> {
  constructor(initialValue: string = 'last') {
    super({
      name: 'last_year_available_to_user',
      initialValue,
      parentFilterSettings: undefined,
      uiSettings: new DashboardFilterUISettings({
        label: 'last_year_available_to_user',
        showInFilterComponent: false,
      }),
      applicator: new RemoteFilterApplicator({remoteFilterKey: 'year'})
    });
  }

  override isValueValid(value: any): boolean {
    return true;
  }
}

/*export class DashboardKKWMethodologySingleSelectFilter extends DashboardMethodologySingleSelectFilter {
  constructor(localDatasetsService: LocalDatasetsService, {defaultValue}: { defaultValue?: string }) {
    super(localDatasetsService, {defaultValue: defaultValue, onlyShow: ['1', '2']});
  }
}*/


export class DashboardMethodologyFromRouteSingleSelectFilter extends DashboardMethodologySingleSelectFilter {

  constructor(localDatasetsService: LocalDatasetsService, route: ActivatedRoute) {
    const data: Data = route.snapshot.data
    // const selectedProduct: Product | undefined = store.selectSnapshot(state => state.products.selectedProduct);
    // const methodologies = selectedProduct?.productMethodologies.map(pm => pm?.methodologyId) ?? [];
    super(localDatasetsService, { defaultValue: data['defaultMethodology'], onlyShow: data['methodologies'] });
  }
}


export class DashboardMethodologyFromActivatedRouteSingleSelectFilter extends DashboardMethodologySingleSelectFilter {
  constructor(
    localDatasetsService: LocalDatasetsService,
    store: Store,
    activatedRoute: ActivatedRoute,
    methodologiesLimiter: ProductMethodologiesLimiterService,
    { defaultValue, showInFilterComponent = true, autoDisplayFirst, filter }: { defaultValue?: string, autoDisplayFirst?: boolean, showInFilterComponent?: boolean, filter?: boolean, limitToMethodologies?: string[] }
  ) {
    const selectedProduct = store.selectSnapshot(state => state.products.selectedProduct) as Product | undefined;
    const dashboardId = activatedRoute.snapshot.url[0].path;
    const methodologiesToShow = methodologiesLimiter.fromDashboardId(selectedProduct, dashboardId);

    super(localDatasetsService, {
      onlyShow: methodologiesToShow,
      defaultValue: defaultValue ?? selectedProduct?.defaultMethodologyId?.toString(),
      showInFilterComponent,
      autoDisplayFirst,
      filter,
    });
  }
}

export class DashboardMethodologyFromStoreSingleSelectFilter extends DashboardMethodologySingleSelectFilter {

  constructor(localDatasetsService: LocalDatasetsService, store: Store, {
    defaultValue,
    showInFilterComponent = true,
    autoDisplayFirst,
    filter,
    limitToMethodologies,
    width
  }: {
    defaultValue?: string,
    autoDisplayFirst?: boolean,
    showInFilterComponent?: boolean,
    filter?: boolean,
    limitToMethodologies?: string[],
    width?: string,
  }) {
    const selectedProduct: Product | undefined = store.selectSnapshot(state => state.products.selectedProduct);
    const productMethodologies = (selectedProduct?.productMethodologies.map(pm => pm!.methodologyId.toString())) ?? [] as string[];

    let methodologies = productMethodologies;
    if (limitToMethodologies && (limitToMethodologies.filter(m => productMethodologies.includes(m))).length > 0) {
      const oneOfLimitedMethodologiesIsIncludedInProductMethodologies = limitToMethodologies.filter(m => productMethodologies.includes(m)).length > 0;
      if (oneOfLimitedMethodologiesIsIncludedInProductMethodologies) {
        methodologies = productMethodologies.filter(m => limitToMethodologies ? limitToMethodologies.includes(m) : true);
      }
    }

    super(localDatasetsService, {
      onlyShow: methodologies,
      defaultValue: defaultValue ?? selectedProduct?.defaultMethodologyId?.toString(),
      showInFilterComponent,
      autoDisplayFirst,
      filter,
      width
    });
  }
}


export class DashboardMetricSingleSelectFilter extends DashboardSingleSelectFilter<string> {
  constructor({ options, defaultValue, uiSettings }: {
    options: DashboardFilterSelectOption[],
    defaultValue: string,
    uiSettings: SingleSelectFilterUISettings
  }) {
    super({
      name: 'metric',
      options: options,
      settings: new SingleSelectFilterSettings({}),
      uiSettings: uiSettings,
      // applicator: new LocalRowColumnSingleFilterValueFilterApplicator('metric'),
      initialValue: defaultValue,
    });
  }
}

export class DashboardCompanyTypeMetricSingleSelectFilter extends DashboardMetricSingleSelectFilter {
  constructor(options?: { showInFilterComponent?: boolean, excludedOptions?: string[] }) {
    const excludedOptions = options?.excludedOptions ?? []
    super({
      options: [
        {
          label: 'Unternehmen',
          value: 'companies',
        },
        {
          label: 'Kleinunternehmen',
          value: 'small_companies',
        },
      ].filter(opt => !excludedOptions.includes(opt.value)),
      defaultValue: 'companies',
      uiSettings: new SingleSelectFilterUISettings({
        label: 'Unternehmenstyp',
        showInFilterComponent: options?.showInFilterComponent ?? true
      }),
    });
  }
}

export class DashboardEmployedTypeMetricSingleSelectFilter extends DashboardSingleSelectFilter<string> {
  constructor(options?: { showWhenDisabled?:boolean, showInFilterComponent?:boolean }) {
    super({
      name: 'employed',
      options: [
        {
          label: 'Gesamtbeschäftigte',
          value: 'total',
        },
        {
          label: 'Sv-pflichtig Beschäftigte',
          value: 'social_security',
        },
        {
          label: 'Geringfügig Beschäftigte',
          value: 'marginal'
        }
      ],
      uiSettings: new SingleSelectFilterUISettings({
        label: 'Beschäftigten-Kategorie',
        showInFilterComponent: options?.showInFilterComponent ?? true,
        showWhenDisabled: options?.showWhenDisabled
      }),
      settings: new SingleSelectFilterSettings({}),
      initialValue: 'total'
    });
  }
}

export class DashboardGrossValueAddedTypeMetricSingleSelectFilter extends DashboardSingleSelectFilter<string> {
  constructor(options?: { showWhenDisabled?:boolean, showInFilterComponent?:boolean }) {
    super({
      name: 'gross_value_added',
      options: [
        {
          label: 'Bruttowertschöpfung',
          value: 'gross_value_added',
        },
        {
          label: 'Produktionswert',
          value: 'production_value',
        }
      ],
      uiSettings: new SingleSelectFilterUISettings({
        label: 'Kennzahl',
        showInFilterComponent: options?.showInFilterComponent ?? true,
        showWhenDisabled: options?.showWhenDisabled
      }),
      settings: new SingleSelectFilterSettings({}),
      initialValue: 'gross_value_added'
    });
  }
}

export class DashboardRevenueIndexValueSingleSelectFilter extends DashboardSingleSelectFilter<string> {
  constructor(config?: {
    excludedOptions?: string[]
  }) {
    const excludedOptions = config?.excludedOptions ?? []

    super({
      name: 'revenue-index',
      options: [
        {
          label: 'Umsatz pro Unternehmen',
          value: 'revenue_per_company__k_eur',
        },
        {
          label: 'Umsatz pro Kleinunternehmen',
          value: 'revenue_per_small_company__k_eur',
        }
      ].filter(opt => !excludedOptions.includes(opt.value)),
      uiSettings: new SingleSelectFilterUISettings({
        label: 'Indizierter Wert',
      }),
      settings: new SingleSelectFilterSettings({}),
      initialValue: 'revenue_per_company__k_eur'
    });
  }
}

export class DashboardGrossValueAddedIndexValueSingleSelectFilter extends DashboardSingleSelectFilter<string> {
  constructor(config?: {
    excludedOptions?: string[]
  }) {
    const excludedOptions = config?.excludedOptions ?? []

    super({
      name: 'gross-value-added-index',
      options: [
        {
          label: 'Anteil Bruttowertschöpfung am Produktionswert',
          value: 'gross_value_added_production_value_pct',
        },
        {
          label: 'Bruttowertschöpfung pro Unternehmen insgesamt',
          value: 'gross_value_added_per_total_company__k_eur',
        },
        {
          label: 'Bruttowertschöpfung pro Gesamtbeschäftigten',
          value: 'gross_value_added_per_employed_total__k_eur',
        },
        {
          label: 'Bruttowertschöpfung pro SV-pflichtig Beschäftigten',
          value: 'gross_value_added_per_employed_social_security__k_eur',
        },
        {
          label: 'Bruttowertschöpfung pro Erwerbstätigen',
          value: 'gross_value_added_per_economic_participants_total__k_eur',
        },
        {
          label: 'Bruttowertschöpfung pro Kernerwerbstätigen',
          value: 'gross_value_added_per_economic_participants_core_market__k_eur',
        },
      ].filter(opt => !excludedOptions.includes(opt.value)),
      uiSettings: new SingleSelectFilterUISettings({
        label: 'Indizierter Wert',
      }),
      settings: new SingleSelectFilterSettings({}),
      initialValue: 'gross_value_added_per_total_company__k_eur'
    });
  }
}

export class DashboardEconomicParticipantsIndexValueSingleSelectFilter extends DashboardSingleSelectFilter<string> {
  constructor(args: {
    excludedOptions: string[]
  } = {
    excludedOptions: []
  }) {
    super({
      name: 'economic-participants-index',
      options: [
        {
          label: 'Erwerbstätige pro Unternehmen',
          value: 'economic_participants_total_per_company',
        },
        {
          label: 'Umsatz pro Erwerbstätigen',
          value: 'revenue_per_economic_participants_total__k_eur',
        },
        {
          label: 'Anteil Erwerbstätiger an der Bevölkerung',
          value: 'economic_participants_total_population_pct',
        },
        {
          label: 'Anteil Beschäftigter an Erwerbstätigen',
          value: 'employed_total_economic_participants_total_pct',
        }
      ].filter(opt => !args.excludedOptions.includes(opt.value)),
      uiSettings: new SingleSelectFilterUISettings({
        label: 'Indizierter Wert',
      }),
      settings: new SingleSelectFilterSettings({}),
      initialValue: 'economic_participants_total_per_company'
    });
  }
}

export class DashboardEmployedIndexValueSingleSelectFilter extends DashboardSingleSelectFilter<string> {
  constructor({ excluded, defaultValue }: { excluded?: string[], defaultValue?: string, }) {
    const options = [
      {
        label: 'Beschäftigte pro Unternehmen',
        value: 'employed_per_company',
      },
      {
        label: 'Anteil Teilmarktbeschäftigte an Beschäftigten (branchenübergreifend)',
        value: 'employed_state_pct',
      },
      {
        label: 'Anteil Teilmarktbeschäftigte an Beschäftigung (branchenübergreifend)',
        value: 'employed_district_pct',
      },
      {
        label: 'Anteil Teilmarktbeschaftigte an Bevolkerung',
        value: 'employed_population_pct'
      }
    ]
    const availableOptions = excluded && excluded.length ? options.filter(option => !excluded.includes(option.value)) : options
    super({
      name: 'employed-index',
      options: availableOptions,
      uiSettings: new SingleSelectFilterUISettings({
        label: 'Indizierter Wert',
      }),
      settings: new SingleSelectFilterSettings({}),
      initialValue: defaultValue ?? availableOptions[0].value
    });
  }
}

export class DashboardWzClassesMultiSelectFilter extends DashboardMultiSelectFilter<string> {
  constructor(private localDatasetsService: LocalDatasetsService, {
    allInitiallySelected = true,
    allInitiallySelectedExceptValue = []
  }: { allInitiallySelected?: boolean, allInitiallySelectedExceptValue?: string[] }) {
    const options = localDatasetsService.getWzClassOptions();
    super({
      name: 'wz_classes',
      options: options,
      settings: new MultiSelectFilterSettings({
        allInitiallySelected: allInitiallySelected,
        allInitiallySelectedExceptValue: allInitiallySelectedExceptValue,
        preserveSingleSelectionOnParentChange: false,
        fallbackSingleSelectionToFirstOption: true,
      }),
      uiSettings: new MultiSelectFilterUISettings({
        label: 'WZ-Klassen',
        maxSelectedLabels: 1,
        selectedItemsLabel: 'Mehrere WZ-Klassen',
      }),
      applicator: new LocalRowColumnArrayFilterValueFilterApplicator('wz_id'),
      initialValue: [],
      parentFilterSettings: new SingleSelectParentSelectChildFilterSettings({
        parentFilterName: 'sub_market',
        filterByKey: 'sub_market_id',
      })
    });
  }
}

export class DashboardGenderTypeMetricSingleSelectFilter extends DashboardSingleSelectFilter<string> {
  constructor() {
    super({
      name: 'gender',
      options: [
        {
          label: 'Frauen',
          value: 'female',
        },
        {
          label: 'Männer',
          value: 'male',
        },
      ],
      uiSettings: new SingleSelectFilterUISettings({
        label: 'Geschlecht',
      }),
      settings: new SingleSelectFilterSettings({}),
      initialValue: 'female',
    });
  }
}
export class DashboardRankSliderFilter extends DashboardSliderFilter<number> {
  constructor({
    minRank,
    maxRank,
    defaultValue,
    name = 'rank-slider',
    applicator,
    showInFilterComponent = true
  }: {
    minRank: number,
    maxRank: number,
    defaultValue?: number,
    name?: string,
    applicator?: DashboardFilterApplicator<number[], DSVRowArray<string>, DSVRowString>,
    showInFilterComponent?: boolean
  }) {
    if (minRank > maxRank) {
      throw new Error('minRank must be less than or equal to maxRank');
    }

    if (defaultValue != null && defaultValue < minRank) {
      throw new Error('defaultValue must be greater than or equal to minRank');
    }

    if (defaultValue != null && defaultValue > maxRank) {
      throw new Error('defaultValue must be less than or equal to maxRank');
    }

    super({
      name: name,
      initialValue: defaultValue ?? maxRank,
      parentFilterSettings: undefined,
      uiSettings: {
        label: 'Rank',
        showInFilterComponent: showInFilterComponent,
      },
      settings: {
        min: minRank,
        max: maxRank,
      },
      applicator
    });
  }
}

export class DashboardForecastSingleSelectFilter extends DashboardSingleSelectFilter<string | undefined> {
  constructor(private localDatasetsService: LocalDatasetsService,
    {
      name = 'forecast',
      defaultValue,
      onlyShow,
      applicator = new LocalRowColumnSingleFilterValueFilterApplicator('forecast_method_id', (row: DSVRowString, filterValue: string | number) => row['forecast_method_id'] === ''),
      showInFilterComponent = true
    }: {
      name?: string,
      defaultValue?: string,
      onlyShow?: string[],
      applicator?: DashboardFilterApplicator<string, DSVRowArray<string>, DSVRowString>,
      showInFilterComponent?: boolean
  }) {
    const forecastOptions = [
      {
        label: 'Goldmedia Standard',
        value: '1'
      },
      {
        label: 'Goldmedia Linear',
        value: '2'
      },
      {
        label: 'DevineDela Auto',
        value: '3'
      }
    ]

    const forecastOptionsShown = onlyShow
      ? forecastOptions.filter(({value}) => onlyShow.includes(value))
      : forecastOptions

    super({
      name,
      options: forecastOptionsShown,
      settings: new SingleSelectFilterSettings({}),
      uiSettings: new SingleSelectFilterUISettings({
        label: 'Methode',
        showInFilterComponent
      }),
      applicator,
      initialValue: defaultValue
    });
  }
}

export class DashboardForecastFromActivatedRouteSingleSelectFilter extends DashboardSingleSelectFilter<string | undefined> {
  constructor(private router: Router, private localDatasetsService: LocalDatasetsService,
    {
      name = 'forecast',
      defaultValue,
      onlyShow,
      applicator = new LocalRowColumnSingleFilterValueFilterApplicator('forecast_method_id', (row: DSVRowString, filterValue: string | number) => row['forecast_method_id'] === ''),
      showInFilterComponent = true
    }: {
      name?: string,
      defaultValue?: string,
      onlyShow?: string[],
      applicator?: DashboardFilterApplicator<string, DSVRowArray<string>, DSVRowString>,
      showInFilterComponent?: boolean
  }) {
    const forecastOptions = [
      {
        label: 'Goldmedia Standard',
        value: '1'
      },
      {
        label: 'Goldmedia Linear',
        value: '2'
      },
      {
        label: 'DevineDela Auto',
        value: '3'
      }
    ]

    const forecastOptionsShown = onlyShow
      ? forecastOptions.filter(({value}) => onlyShow.includes(value))
      : forecastOptions

    // set initial value based on the selected product (1 for product 1, otherwise 3),
    // but still allow to override it with the defaultValue set at dashboard level
    const productId = getProductIdFromDashboardPath(router.routerState.snapshot.url);
    let initialValueByProductId;
    switch (productId) {
      case '':
        initialValueByProductId = '';
        break;
      case '1':
        initialValueByProductId = '1';
        break;
      default:
        initialValueByProductId = '3';
        break;
    }

    const initialValueToUse = defaultValue ?? initialValueByProductId;

    super({
      name,
      options: forecastOptionsShown,
      settings: new SingleSelectFilterSettings({}),
      uiSettings: new SingleSelectFilterUISettings({
        label: 'Methode',
        showInFilterComponent
      }),
      applicator,
      initialValue: initialValueToUse
    });
  }
}

export class DashboardChartTypeSingleSelectFilter extends DashboardSingleSelectFilter<string | undefined> {
  constructor(private localDatasetsService: LocalDatasetsService, { defaultValue }: { defaultValue?: string }) {
    super({
      name: 'chart_type',
      options: [
        {
          label: 'Balkendiagramm',
          value: 'bar'
        },
        {
          label: 'Liniendiagramm',
          value: 'line'
        }
      ],
      settings: new SingleSelectFilterSettings({}),
      uiSettings: new SingleSelectFilterUISettings({
        label: 'Ansicht',
      }),
      applicator: undefined,
      initialValue: defaultValue,
    });
  }
}

export class DashboardMapLevelSingleSelectFilter extends DashboardSingleSelectFilter<string | undefined> {
  constructor(private localDatasetsService: LocalDatasetsService, { defaultValue, excluded }: { defaultValue?: string, excluded?: string[] }) {
    super({
      name: 'map_level',
      options: [
        {
          label: 'Regionalverbände',
          value: 'regional_associations'
        },
        {
          label: 'Regierungsbezirk',
          value: 'administrative_districts'
        },
        {
          label: 'Land-/Stadtkreise',
          value: 'districts'
        }
      ].filter(opt => !excluded?.includes(opt.value)),
      settings: new SingleSelectFilterSettings({}),
      uiSettings: new SingleSelectFilterUISettings({
        label: 'Ebene',
      }),
      applicator: undefined,
      initialValue: defaultValue,
    });
  }
}

export class DashboardExportSideSingleSelectFilter extends DashboardSingleSelectFilter<string> {
  constructor({
    name = 'export_side',
    showInFilterComponent = true
  }: {
    name?: string,
    applicator?: DashboardFilterApplicator<string, DSVRowArray<string>, DSVRowString>,
    showInFilterComponent: boolean
  }) {
    super({
      name,
      options: [
        { label: 'EXPORT A', value: 'A' },
        { label: 'EXPORT B', value: 'B' }
      ],
      settings: new SingleSelectFilterSettings({}),
      uiSettings: new SingleSelectFilterUISettings({
        label: '',
        showInFilterComponent,
      }),
      initialValue: 'A',
    });
  }
}

export class DashboardWzLayerVisibilitySingleSelectFilter extends DashboardSingleSelectFilter<string> {
  constructor(private localDatasetsService: LocalDatasetsService, {
    name = 'wz_visibility',
    label = 'Darstellung',
    defaultValue = 'true',
    showInFilterComponent,
    showWhenDisabled
  }: {
    name?: string,
    label?: string,
    defaultValue?: string,
    showInFilterComponent?: boolean,
    showWhenDisabled?: boolean
  }) {
    const options = [
      { label: 'mit WZ-Klasse', value: 'true' },
      { label: 'ohne WZ-Klasse', value: 'false' },
    ]

    super({
      name,
      options: options,
      settings: new SingleSelectFilterSettings({}),
      uiSettings: new SingleSelectFilterUISettings({
        label,
        showInFilterComponent: showInFilterComponent,
        showWhenDisabled: showWhenDisabled,
      }),
      applicator: undefined,
      initialValue: defaultValue,
    });
  }
}

export class DashboardEconomicParticipantMetricSingleSelectFilter extends DashboardSingleSelectFilter<string> {
  constructor(options?: { initialValue?: 'total' | 'core_market' | 'marginal', showWhenDisabled?:boolean, showInFilterComponent?:boolean, excludedOptions?: string[] }) {
    super({
      name: 'economic_participant',
      options: [
        {
          label: 'Erwerbstätige',
          value: 'total',
        },
        {
          label: 'Kernerwerbstätige',
          value: 'core_market',
        },
        {
          label: 'Geringfügig Erwerbstätige',
          value: 'marginal'
        }
      ].filter(opt => options?.excludedOptions ? !options.excludedOptions.includes(opt.value) : true),
      uiSettings: new SingleSelectFilterUISettings({
        label: 'Erwerbstätigen-Kategorie',
        showInFilterComponent: options?.showInFilterComponent ?? true,
        showWhenDisabled: options?.showWhenDisabled
      }),
      settings: new SingleSelectFilterSettings({}),
      initialValue: options?.initialValue ?? 'total'
    });
  }
}
export class DashboardTouristTypeSingleSelectFilter extends DashboardSingleSelectFilter<string> {
  constructor(options?: { showWhenDisabled?:boolean, showInFilterComponent?:boolean }) {
    super({
      name: 'tourists',
      options: [
        {
          label: 'Gesamt',
          value: 'total'
        },
        {
          label: 'Ausländisch',
          value: 'foreign',
        },
        {
          label: 'Inländisch',
          value: 'domestic',
        },
      ],
      uiSettings: new SingleSelectFilterUISettings({
        label: 'Touristentyp',
        showInFilterComponent: options?.showInFilterComponent ?? true,
        showWhenDisabled: options?.showWhenDisabled
      }),
      settings: new SingleSelectFilterSettings({}),
      initialValue: 'total'
    });
  }
}

function getProductIdFromDashboardPath(path: string): string {
  const productIdSegment = path.match(/\/dashboards\/(\d+)\//);
  const productId = productIdSegment ? productIdSegment[1] : '';
  return productId;
}
